import { z } from 'zod';
import { validateThumbnail } from './images.ts';

/** ISO date `YYYY-MM-DD` */
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ожидается дата YYYY-MM-DD');
export const currencyCode = z.string().min(3).max(5).toUpperCase();
export const money = z.coerce.number().finite();
const id = z.coerce.number().int().positive();
const optId = id.nullable().optional();
/** generated by the client; a retried create with the same value returns the existing record */
const clientId = z.string().uuid().optional();

export const walletTypes = ['cash', 'bank', 'crypto', 'savings', 'investment', 'other'] as const;
export const categoryKinds = ['income', 'expense', 'debt', 'repayment', 'loan', 'debt_collection', 'transfer'] as const;
export const periodSections = ['mandatory', 'optional', 'foreign'] as const;
export const wishStatuses = ['active', 'bought', 'dropped'] as const;

export const loginInput = z.object({
  email: z.string().email().max(254),
  password: z.string().min(1).max(1024),
  device: z.string().max(100).optional(),
  /** web clients get the refresh token as an httpOnly cookie instead of in the body */
  cookie: z.boolean().optional(),
});

export const walletInput = z.object({
  name: z.string().trim().min(1).max(100),
  currency: currencyCode,
  type: z.enum(walletTypes).default('bank'),
  initialBalance: money.default(0),
  color: z.string().max(20).optional(),
  icon: z.string().max(40).optional(),
  archived: z.boolean().optional(),
  excludeFromTotal: z.boolean().optional(),
  /** stays in balances and pickers, but collapsed under «Скрытые» in every list */
  hidden: z.boolean().optional(),
  sort: z.number().int().optional(),
});

const publicHttpsUrl = z.string().url().max(2048).refine((value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch { return false; }
}, 'Нужна ссылка HTTPS');

export const iconAttributionInput = z.object({
  source: z.enum(['iconify', 'wikimedia', 'url']),
  url: publicHttpsUrl,
  author: z.string().max(1000),
  license: z.string().max(200),
  licenseUrl: publicHttpsUrl.optional(),
});

export const categoryInput = z.object({
  name: z.string().trim().min(1).max(100),
  kind: z.enum(categoryKinds),
  parentId: optId,
  icon: z.string().max(40).optional(),
  /** own picture: a small square image as a data URL (resized on the device); null removes it */
  iconImage: z.string().max(120_000).regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+\/=]+$/, 'Картинка должна быть PNG, JPEG или WebP').refine(validateThumbnail, 'Нужна корректная PNG, JPEG или WebP миниатюра до 96×96').nullable().optional(),
  iconAttribution: iconAttributionInput.nullable().optional(),
  color: z.string().max(20).optional(),
  archived: z.boolean().optional(),
  /** operations in this category count as investment contributions (income kind: withdrawals) */
  investment: z.boolean().optional(),
  sort: z.number().int().optional(),
});

export const contactInput = z.object({
  name: z.string().trim().min(1).max(100),
  note: z.string().max(1000).optional(),
});

/**
 * `amount` is the absolute value; the sign is derived from the category kind
 * (expense/repayment/loan → negative). Pass `signedAmount` to override.
 */
/** another person's part of a shared operation: they owe it back to the owner */
export const shareInput = z.object({
  /** an existing share being kept or changed */
  debtId: optId,
  contactId: optId,
  contactName: z.string().trim().max(100).optional(),
  amount: money.positive(),
}).refine((s) => !!s.debtId || !!s.contactId || !!s.contactName, { message: 'Укажите человека', path: ['contactName'] });
const shares = z.array(shareInput).max(30);
export const sharesInput = z.object({ shares });

export const transactionInput = z.object({
  walletId: id,
  categoryId: id,
  amount: money.refine((v) => v !== 0, 'Сумма не может быть 0'),
  date: isoDate,
  note: z.string().max(2000).optional(),
  contactId: optId,
  debtId: optId,
  eventId: optId,
  /** the line of the period plan this operation pays */
  periodItemId: optId,
  excludeFromReport: z.boolean().optional(),
  /** spends the period's free money; omitted on create = true */
  inPeriod: z.boolean().optional(),
  /** an income adds to the period's free money; omitted on create = false */
  periodIncome: z.boolean().optional(),
  /** other people's parts, accepted only when the operation is created */
  shares: shares.optional(),
  clientId,
});

export const transferInput = z.object({
  fromWalletId: id,
  toWalletId: id,
  amount: money.positive(),
  /** amount credited to the destination wallet (differs when currencies differ) */
  amountTo: money.positive().optional(),
  /** fee charged on the source wallet, booked as a separate expense */
  fee: money.nonnegative().optional(),
  date: isoDate,
  note: z.string().max(2000).optional(),
  /** the line of the period plan this transfer pays (e.g. money put aside into a goal wallet) */
  periodItemId: optId,
  /** the transfer puts the line's money aside on the destination wallet until the bill is paid from there */
  reserve: z.boolean().optional(),
  /** spends the period's free money; omitted = decided by the wallets (finance/domain/budget.ts transferInPeriod) */
  inPeriod: z.boolean().optional(),
  periodIncome: z.boolean().optional(),
  shares: shares.optional(),
  clientId,
});

/** editing a transfer: both legs are rewritten; the fee is its own expense and is edited there */
export const transferUpdateInput = transferInput.omit({ fee: true, reserve: true, shares: true, clientId: true });

export const debtInput = z.object({
  direction: z.enum(['borrowed', 'lent']),
  title: z.string().trim().min(1).max(200),
  contactId: optId,
  contactName: z.string().trim().max(100).optional(),
  currency: currencyCode,
  principal: money.nonnegative(),
  totalPayable: money.nonnegative().nullable().optional(),
  paidOffset: money.optional(),
  monthlyPayment: money.nonnegative().nullable().optional(),
  startDate: isoDate.optional(),
  dueDate: isoDate.nullable().optional(),
  note: z.string().max(2000).optional(),
  url: z.string().max(1000).optional(),
  closedAt: isoDate.nullable().optional(),
});

export const goalInput = z.object({
  name: z.string().trim().min(1).max(200),
  targetAmount: money.positive(),
  currency: currencyCode,
  walletId: optId,
  manualAmount: money.optional(),
  deadline: isoDate.nullable().optional(),
  color: z.string().max(20).optional(),
  archived: z.boolean().optional(),
  sort: z.number().int().optional(),
});

export const eventInput = z.object({
  date: isoDate,
  title: z.string().trim().min(1).max(200),
  amount: money.default(0),
  currency: currencyCode,
  note: z.string().max(2000).optional(),
  done: z.boolean().optional(),
});

export const foreignExpenseInput = z.object({
  name: z.string().trim().min(1).max(200),
  amount: money.nonnegative(),
  currency: currencyCode,
  note: z.string().max(2000).optional(),
  sort: z.number().int().optional(),
});

export const wishlistInput = z.object({
  name: z.string().trim().min(1).max(200),
  price: money.nonnegative().nullable().optional(),
  currency: currencyCode,
  priority: z.number().int().min(1).max(5).default(3),
  url: z.string().max(1000).optional(),
  pros: z.string().max(4000).optional(),
  cons: z.string().max(4000).optional(),
  status: z.enum(wishStatuses).optional(),
  sort: z.number().int().optional(),
});

export const periodInput = z.object({
  startDate: isoDate,
  endDate: isoDate,
  budget: money.default(0),
  note: z.string().max(2000).optional(),
});

/** create a period for the given date using the user's payday schedule and templates */
export const periodGenerateInput = z.object({
  date: isoDate,
  budget: money.optional(),
  fromTemplate: z.boolean().default(true),
});

/** a person's planned part of a plan line; paying the line creates the shares of its operation */
export const planShareInput = z.object({
  contactId: optId,
  contactName: z.string().trim().max(100).optional(),
  amount: money.positive(),
  /** a period line's existing share being kept */
  debtId: optId,
}).refine((s) => !!s.debtId || !!s.contactId || !!s.contactName, { message: 'Укажите человека', path: ['contactName'] });

const itemBase = {
  section: z.enum(periodSections),
  name: z.string().trim().min(1).max(200),
  amount: money,
  currency: currencyCode,
  categoryId: optId,
  debtId: optId,
  goalId: optId,
  /** a transfer line: the wallet the money goes to */
  walletId: optId,
  highlight: z.boolean().optional(),
  sort: z.number().int().optional(),
  shares: z.array(planShareInput).max(30).optional(),
};

export const periodItemInput = z.object({
  ...itemBase,
  included: z.boolean().optional(),
  done: z.boolean().optional(),
});

export const periodTemplateInput = z.object({
  ...itemBase,
  slot: z.number().int().min(0).max(30),
});

export const rateInput = z.object({
  currency: currencyCode,
  date: isoDate,
  rate: money.positive(),
});

export const settingsInput = z.object({
  name: z.string().max(100).optional(),
  baseCurrency: currencyCode.optional(),
  periodStartDays: z.array(z.number().int().min(1).max(28)).min(1).max(4).optional(),
  /** shown first on the overview and preselected for a new operation */
  primaryWalletId: optId,
  /** display order of wallet groups on the wallets page; types not listed follow in the default order */
  walletTypeOrder: z.array(z.enum(walletTypes)).max(walletTypes.length).optional(),
});

/** new display order of wallets (within their groups): ids in order, sort = position */
export const reorderInput = z.object({ ids: z.array(id).min(1).max(500) });

/** a typed or dictated phrase for the local request parser; longer text would be cut by the model */
export const assistantParseInput = z.object({ text: z.string().trim().min(1).max(300) });

export const passwordChangeInput = z.object({
  currentPassword: z.string().min(1).max(1024),
  newPassword: z.string().min(8).max(1024),
});

export const finplanSettingsInput = z.object({
  startCapital: money,
  monthlyIncome: money,
  monthlyExpenses: money,
  incomeGrowth: z.number().min(-1).max(5),
  investRate: z.number().min(-1).max(5),
  inflation: z.number().min(-1).max(5),
  firstYear: z.number().int().min(1900).max(2200),
  lastYear: z.number().int().min(1900).max(2300),
  ageInFirstYear: z.number().int().min(0).max(150),
  baseYear: z.number().int().min(1900).max(2300),
});

export const finplanYearInput = z.object({
  oneTimeExpenses: money.default(0),
  passiveRate: z.number().min(-1).max(5).nullable().optional(),
  note: z.string().max(1000).optional(),
});

/** broker facts for a plan year; null clears a figure */
export const finplanMarketInput = z.object({
  marketValue: money.nullable(),
  marketResult: money.nullable(),
  marketDate: isoDate.nullable(),
});

export const aiReportTypes = ['period', 'month', 'anomalies', 'subscriptions', 'forecast', 'year'] as const;
export const aiReportInput = z.object({
  type: z.enum(aiReportTypes),
  scope: z.enum(['current', 'previous']).default('current'),
});
export const aiQuestionScopes = ['auto', 'month', 'quarter', 'year'] as const;
/** a free question about the user's money; the model first picks the data, the server computes it */
export const aiQuestionInput = z.object({
  text: z.string().trim().min(3).max(500),
  scope: z.enum(aiQuestionScopes).default('auto'),
});
/** screenshots of the broker app as data URLs; the browser re-encodes them as JPEG before sending */
export const aiBrokerInput = z.object({
  images: z.array(z.string().max(4_300_000).regex(/^data:image\/(?:jpeg|png|webp);base64,[A-Za-z0-9+/]+=*$/)).min(1).max(3),
});

export const transactionQuery = z.object({
  walletId: id.optional(),
  categoryId: id.optional(),
  debtId: id.optional(),
  contactId: id.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  q: z.string().max(200).optional(),
  kind: z.enum(categoryKinds).optional(),
  /** only operations marked «не учитывать в отчётах» */
  excluded: z.enum(['1']).optional(),
  /** only non-transfer operations included in reports */
  included: z.enum(['1']).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(100),
  offset: z.coerce.number().int().min(0).default(0),
}).refine(q => !(q.excluded && q.included), { message: 'Выберите один фильтр учёта в отчётах', path: ['included'] });

export const reportQuery = z.object({
  from: isoDate,
  to: isoDate,
  walletId: id.optional(),
});

export const categoryReportQuery = z.object({
  categoryId: id,
  months: z.coerce.number().int().min(3).max(24).default(12),
  walletId: id.optional(),
});

export const importFilesInput = z.object({
  files: z.array(z.object({ name: z.string(), content: z.string() })).min(1).max(50),
  /** delete previously imported Money Lover data before importing */
  replace: z.boolean().default(false),
});

export type WalletInput = z.infer<typeof walletInput>;
export type CategoryInput = z.infer<typeof categoryInput>;
export type TransactionInput = z.infer<typeof transactionInput>;
export type TransferInput = z.infer<typeof transferInput>;
export type DebtInput = z.infer<typeof debtInput>;
export type GoalInput = z.infer<typeof goalInput>;
export type EventInput = z.infer<typeof eventInput>;
export type WishlistInput = z.infer<typeof wishlistInput>;
export type PeriodItemInput = z.infer<typeof periodItemInput>;
export type PeriodTemplateInput = z.infer<typeof periodTemplateInput>;
export type FinplanSettingsInput = z.infer<typeof finplanSettingsInput>;
export type TransactionQuery = z.infer<typeof transactionQuery>;
export type ShareInput = z.infer<typeof shareInput>;
export type TransferUpdateInput = z.infer<typeof transferUpdateInput>;
export type PlanShareInput = z.infer<typeof planShareInput>;

export const debtPaymentInput = z.object({ walletId: id, amount: money.refine(v => v !== 0, 'Сумма не может быть 0'), date: isoDate, note: z.string().max(2000).optional() });
export const openDebtInput = z.object({ transaction: transactionInput.extend({ clientId: z.string().uuid() }), debt: debtInput });
